import { AppUrls } from '@/urls';
import React, { useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { apiSdk } from '@/httpClient';
import FormItem from '@/components/Form/FormItem';
import { LoginResponseModel } from '@/__generated';
import moment from 'moment';
import authService from '@/services/authService';
import { useAppContext } from '@/contexts/AppContext';
import { getUrlQueryValue } from '@/hooks/useQuery';
import { UNKNOWN_ERROR_MESSAGE } from '@/config';
import Spin from '@/components/Spin';
import { FormValidation } from '@/helpers/formValidations';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import PasswordInput from '@/components/Form/PasswordInput';

export default function SsoLoginPage() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<any>();
  const returnUrl = getUrlQueryValue('ReturnUrl');
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const onSubmit = (values: any) => {
    setLoading(true);
    setError('');
    apiSdk.AgentAccountService.login({
      body: {
        password: values.password,
        userName: values.userName,
      },
    })
      .then(response => {
        if (response && response.success && response.currentUser) {
          apiSdk.MiyabiAuthService.generateAuthToken({
            headers: {
              Authorization: `Bearer ${response.accessToken}`,
            },
          }).then(res => {
            authService.redirect2MiyabiLoginUrl(returnUrl || '', res, false);
          });
        } else {
          setError(t(response.message || UNKNOWN_ERROR_MESSAGE));
        }
      })
      .catch(() => {
        setError(t(UNKNOWN_ERROR_MESSAGE));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <section className="layout-pt-lg layout-pb-lg bg-blue-2" style={{ minHeight: '100vh' }}>
      <Helmet>
        <title>SSO Login</title>
      </Helmet>
      <div className="container">
        <div className="row justify-center">
          <div className="col-xl-6 col-lg-7 col-md-9">
            <Spin loading={loading}>
              <div className="px-50 py-50 sm:px-20 sm:py-20 bg-white shadow-4 rounded-4">
                <AnimatePresence>
                  {error ? (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      style={{ color: '#ef3b7d', fontWeight: 'bold', fontSize: 18 }}
                    >
                      {error}
                    </motion.div>
                  ) : null}
                </AnimatePresence>

                <form className="row y-gap-20" onSubmit={handleSubmit(onSubmit)}>
                  <div className="col-12">
                    <h1 className="text-22 fw-500">Login with Omega</h1>
                  </div>

                  <div className="col-12">
                    <FormItem errors={errors} label={t('base.UserName')} name="userName">
                      <input type="text" {...register('userName', FormValidation.Required)} autoComplete="username" />
                    </FormItem>
                  </div>

                  <div className="col-12">
                    <FormItem errors={errors} label={t('base.Password')} name="password">
                      <PasswordInput {...register('password', FormValidation.Required)} autoComplete="current-password" />
                    </FormItem>
                  </div>

                  <div className="col-12">
                    <button type="submit" className="button py-20 -dark-1 bg-blue-1 text-white w-100">
                      {t('base.SignIn')} <div className="icon-arrow-top-right ml-15" />
                    </button>
                  </div>
                </form>
              </div>
            </Spin>
          </div>
        </div>
      </div>
    </section>
  );
}
